import React, { useState, useMemo } from 'react';
import { useStore } from '../store-hybrid';

export const FinanceEditor: React.FC = () => {
  const { fund, cars, updateFundManually } = useStore();
  const [isUnlocked, setIsUnlocked] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentBalance, setCurrentBalance] = useState(fund?.currentBalance?.toString() || '0');
  const [totalBenefit, setTotalBenefit] = useState('0');
  const [totalCharges, setTotalCharges] = useState('0');
  const [isLoading, setIsLoading] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(false);

  // Calculate current values
  const calculatedTotalBenefit = useMemo(() => {
    const transactions = fund?.transactions || [];
    // Calculate net profit from car sales
    const soldCars = cars.filter(car => car.isSold && car.finalSalePrice && car.purchasePrice);
    const totalSalesProfit = soldCars.reduce((sum, car) => {
      const profit = (car.finalSalePrice || 0) - (car.purchasePrice || 0);
      return sum + profit;
    }, 0);

    // Calculate total rental revenue
    const rentalTransactions = transactions.filter(t => t.category === 'rental' && t.type === 'add_money');
    const totalRentalRevenue = rentalTransactions.reduce((sum, t) => sum + t.amount, 0);

    return totalSalesProfit + totalRentalRevenue;
  }, [cars, fund?.transactions]);

  const calculatedTotalCharges = useMemo(() => {
    const transactions = fund?.transactions || [];
    const chargeTransactions = transactions.filter(t => t.type === 'add_charge');
    return chargeTransactions.reduce((sum, t) => sum + t.amount, 0);
  }, [fund?.transactions]);

  // Use manual values if set, otherwise use calculated
  const displayTotalBenefit = fund?.manualTotalBenefit ?? calculatedTotalBenefit;
  const displayTotalCharges = fund?.manualTotalCharges ?? calculatedTotalCharges;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('fr-DZ', {
      style: 'currency',
      currency: 'DZD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const handleUnlockClick = () => {
    setShowPinModal(true);
  };

  const handleEditClick = () => {
    setIsEditing(true);
    setCurrentBalance(fund?.currentBalance?.toString() || '0');
    setTotalBenefit(displayTotalBenefit.toString());
    setTotalCharges(displayTotalCharges.toString());
  };

  const handlePinSubmit = () => {
    if (pinInput === '1610') {
      setShowPinModal(false);
      setIsUnlocked(true);
      setPinError(false);
      setPinInput('');
    } else {
      setPinError(true);
      setTimeout(() => setPinError(false), 2000);
    }
  };

  const handleLock = () => {
    setIsUnlocked(false);
    setIsEditing(false);
  };

  const handleSave = async () => {
    setIsLoading(true);
    try {
      const newBalance = parseFloat(currentBalance) || 0;
      const newTotalBenefit = parseFloat(totalBenefit) || 0;
      const newTotalCharges = parseFloat(totalCharges) || 0;
      await updateFundManually(newBalance, newTotalBenefit, newTotalCharges);
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating fund:', error);
      alert('Failed to update fund. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setCurrentBalance(fund?.currentBalance?.toString() || '0');
  };

  return (
    <>
      {!isUnlocked ? (
        // Locked State - Show Unlock Button
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8 transition-colors duration-300">
          <div className="text-center py-8">
            <div className="text-6xl mb-4">🔒</div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Finance Manager
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
              This section is protected. Enter PIN to access.
            </p>
            <button
              onClick={handleUnlockClick}
              className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2 mx-auto"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0m-4 8v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2z" />
              </svg>
              Unlock Finance Manager
            </button>
          </div>
        </div>
      ) : (
        // Unlocked State - Show Full Component
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-8 transition-colors duration-300">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">💰 Finance Manager</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Manually adjust your financial values
              </p>
            </div>
            <div className="flex items-center gap-2">
              {!isEditing && (
                <button
                  onClick={handleEditClick}
                  className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Edit Finances
                </button>
              )}
              <button
                onClick={handleLock}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors flex items-center gap-2"
                title="Lock Finance Manager"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </button>
            </div>
          </div>

        {isEditing ? (
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                💼 Current Balance
              </label>
              <input
                type="number"
                step="0.01"
                value={currentBalance}
                onChange={(e) => setCurrentBalance(e.target.value)}
                className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-gray-800 dark:text-white"
                placeholder="0"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Current value: {formatCurrency(fund?.currentBalance || 0)}
              </p>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                📈 Total Benefit
              </label>
              <input
                type="number"
                step="0.01"
                value={totalBenefit}
                onChange={(e) => setTotalBenefit(e.target.value)}
                className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-white"
                placeholder="0"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Calculated: {formatCurrency(calculatedTotalBenefit)} | Current: {formatCurrency(displayTotalBenefit)}
              </p>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                💸 Total Charges
              </label>
              <input
                type="number"
                step="0.01"
                value={totalCharges}
                onChange={(e) => setTotalCharges(e.target.value)}
                className="w-full px-4 py-3 text-lg border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 dark:bg-gray-800 dark:text-white"
                placeholder="0"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                Calculated: {formatCurrency(calculatedTotalCharges)} | Current: {formatCurrency(displayTotalCharges)}
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleCancel}
                className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                disabled={isLoading}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isLoading}
                className="flex-1 px-4 py-2 bg-primary-500 hover:bg-primary-600 disabled:bg-gray-400 text-white rounded-lg transition-colors"
              >
                {isLoading ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Current Balance</p>
                  <p className={`text-2xl font-bold mt-1 ${(fund?.currentBalance || 0) >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatCurrency(fund?.currentBalance || 0)}
                  </p>
                </div>
                <div className="text-3xl">💼</div>
              </div>
            </div>

            <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4 border border-blue-200 dark:border-blue-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Benefit</p>
                  <p className={`text-2xl font-bold mt-1 ${displayTotalBenefit >= 0 ? 'text-blue-600 dark:text-blue-400' : 'text-red-600 dark:text-red-400'}`}>
                    {formatCurrency(displayTotalBenefit)}
                  </p>
                  {fund?.manualTotalBenefit !== undefined && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">✏️ Manual override</p>
                  )}
                </div>
                <div className="text-3xl">📈</div>
              </div>
            </div>

            <div className="bg-red-50 dark:bg-red-900/20 rounded-lg p-4 border border-red-200 dark:border-red-800">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600 dark:text-gray-400">Total Charges</p>
                  <p className="text-2xl font-bold mt-1 text-red-600 dark:text-red-400">
                    {formatCurrency(displayTotalCharges)}
                  </p>
                  {fund?.manualTotalCharges !== undefined && (
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">✏️ Manual override</p>
                  )}
                </div>
                <div className="text-3xl">💸</div>
              </div>
            </div>
          </div>
        )}

          {!isEditing && (
            <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg">
              <p className="text-sm text-amber-700 dark:text-amber-300">
                <strong>ℹ️ Info:</strong> Changes are applied directly without creating transaction history.
              </p>
            </div>
          )}
        </div>
      )}

      {/* PIN Modal */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white">🔒 Enter PIN</h3>
                <button
                  onClick={() => {
                    setShowPinModal(false);
                    setPinInput('');
                    setPinError(false);
                  }}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  ✕
                </button>
              </div>

              <div className="text-center space-y-4">
                <div className="text-6xl mb-4">🔐</div>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Enter PIN to access Finance Manager
                </p>
                
                <div className="flex items-center justify-center gap-2">
                  <input
                    type="password"
                    value={pinInput}
                    onChange={(e) => setPinInput(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handlePinSubmit()}
                    placeholder="Enter PIN"
                    maxLength={4}
                    autoFocus
                    className={`w-32 px-4 py-3 text-center text-lg border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent ${
                      pinError ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600 dark:bg-gray-700'
                    }`}
                  />
                  <button
                    onClick={handlePinSubmit}
                    className="px-6 py-3 bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
                  >
                    ✓
                  </button>
                </div>
                
                {pinError && (
                  <p className="text-sm text-red-500 mt-2">
                    ❌ Incorrect PIN
                  </p>
                )}
              </div>

              <div className="mt-6 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                <p className="text-xs text-gray-600 dark:text-gray-400 text-center">
                  This action requires PIN authentication
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

